import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { take } from 'rxjs';

import {
    AddressService,
    BreadcrumbService,
    CalendarService,
    CardSetFooterConfiguration,
    CardSetHeaderConfiguration,
    CardSetMenuConfiguration,
    CardSetTransformationService,
    CoreService,
    DialogService,
    ElementType,
    FileService,
    FormConfigurationRequest,
    FormConfigurationService,
    FormNavigationService,
    HeaderService,
    LicenceService,
    MenuConfigService,
    MenuService
} from 'dspx-core';

import { SYNC_OPERATION_NAMES } from './constants/sync-operation.constants';
import { SYNC_MESSENGER_MESSAGE_TYPES } from './constants/messaging.constants';
import { API_ENDPOINTS, API_ENDPOINT_KEYS, ApiBaseRoute } from './constants/api-endpoints.constants';
import {
  APPLICATION,
  APPLICATION_HEADER_BACKGROUND_COLOUR,
  APPLICATION_STATUS,
  APPLICATION_SUB_TITLE,
  APP_DATA_KEYS,
  AppSelector,
  BreadcrumbSelector,
  CUSTOM_FIELD_TYPE,
  HeaderComponentClasses,
  HeaderComponentSelector,
  POP_UP_CODES,
  POP_UP_TEXTS
} from './constants/app-data.constants';
import { TEMPLATES, VERSIONS } from './constants/templates.constants';

import { SyncRequestMessage, SyncEndedMessage, SyncProgressMessage, SyncStartedMessage, SyncStatusUpdateMessage } from './interfaces/messaging.interface';

import { Logger } from './helpers/logger';
import { LogBuffer } from './helpers/log-buffer';
import { SyncMessenger } from './helpers/sync-messenger';

import { AppDataService } from './services/app-data.service';
import { ServiceWorkerService } from './services/service-worker.service';
import { NetworkConnectivityService } from './services/network-connectivity.service';
import { SyncOperationService } from './services/sync-operation.service';
import { SyncMessengerService } from './services/sync-messenger.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  host: {
    class: 'grid full-height',
    style: 'margin-right: 0;'
  }
})
export class AppComponent implements OnInit, OnDestroy {

  private checkNetworkConnectivityListener: (event: Event) => void;

  private componentClassName: string;
  private logger: Logger;
  private syncMessenger: SyncMessenger;

  private sectionTypes = [
    ElementType.SECTION,
    ElementType.CARDSECTION,
    CUSTOM_FIELD_TYPE.cardSectionCollapsible
  ];

  private pageTypes = [
    ElementType.PAGE,
    ElementType.CARDSETPAGE,
    CUSTOM_FIELD_TYPE.cardSetPageWithCollapsibleSections
  ];

  public readonly repeatingSectionTypes = [
    ElementType.SECTIONREPEATING,
    ElementType.CARDSECTIONREPEATING,
    ElementType.SECTIONREPEATINGINFORMATION,
    CUSTOM_FIELD_TYPE.cardSectionRepeatingCollapsible
  ];

  constructor(
    protected menuConfigService: MenuConfigService,
    private router: Router,
    private appDataService: AppDataService,
    private serviceWorkerService: ServiceWorkerService,
    private networkConnectivityService: NetworkConnectivityService,
    private coreService: CoreService,
    private addressService: AddressService,
    private fileService: FileService,
    private calendarService: CalendarService,
    private formConfigurationService: FormConfigurationService,
    private menuService: MenuService,
    private headerService: HeaderService,
    private cardSetTransformationService: CardSetTransformationService,
    private dialogService: DialogService,
    private formNavigationService: FormNavigationService,
    private licenceService: LicenceService,
    private breadcrumbService: BreadcrumbService,
    private syncOperationService: SyncOperationService,
    syncMessengerService: SyncMessengerService) {

    this.componentClassName = this.constructor.name;
    this.logger = new Logger(this.componentClassName, this.appDataService.getAppData(APP_DATA_KEYS.logLevel));
    this.syncMessenger = syncMessengerService.getSyncMessenger();

    this.syncOperationService.registerHandler(this.componentClassName);
  }

  environmentBannerMessage: string;
  environmentBannerBgColor: string;
  environmentBannerColor: string;
  headerConfig: CardSetHeaderConfiguration;
  footerConfig: CardSetFooterConfiguration;
  menuConfig: CardSetMenuConfiguration;

  public headerLinks: any = {};
  public isMenuVisible: boolean = false;  //Set this to false if you have an app that starts on an unauthenticated component.
  public showLogout: boolean = false;     //Set this to false if you have an app that starts on an unauthenticated component.
  //app-data.service.ts has a function called 'getUserDetails' (that you can modify to your liking) which you can call from
  //an authenticated component to set the header config with the user details and display a menu with the logout option.

  ngOnInit() {

    const logPrefix = 'ngOnInit';
    this.logger.log(`${logPrefix}: enter`);

    this.menuService.menuLoaded$.subscribe((config) => {
      if (config) {
        this.menuConfig = config;
      }
    });

    this.headerService.headerUpdated$.subscribe((config) => {
      if (config) {
        this.headerConfig = config;
      }
    });

    this.menuConfigService.styleConfigService.layoutMode =
      'static'; /*'overlay';*/ /*'horizontal';*/ /*'slim';*/
    this.menuConfigService.styleConfigService.layoutColor = /*'dark';*/ 'light';
    this.menuConfigService.styleConfigService.darkMenu = /*true;*/ false;
    this.menuConfigService.styleConfigService.ripple = true; /*false;*/
    this.menuConfigService.styleConfigService.inputStyle = 'outlined'; /*'filled';*/

    this.menuConfigService.primengConfig.ripple =
      this.menuConfigService.styleConfigService.ripple;

    this.formConfigurationService.endpoints = {
      getFormConfig: API_ENDPOINTS.configuration.default,
      getMaatAuditPlanConfigAndResponse: API_ENDPOINTS.maatAuditPlan.getConfigAndResponse,
      getMaatJobConfigAndResponse: API_ENDPOINTS.maatJob.getConfigAndResponse,
      getMaatJobItemConfigAndResponse: API_ENDPOINTS.maatJobItem.getConfigAndResponse,
      getMaatNcqConfigAndResponse: null, // This changes every time because it contains the maatJobItemId, so it needs to be set before being called
      getMaatNcqConfigWithEmptyResponse: null, // This changes every time because it contains the maatJobItemId, so it needs to be set before being called
      getMaatUserConfigAndResponse: API_ENDPOINTS.maatUser.getConfigAndResponse,
      getMaatSorDescriptionConfigAndResponse: API_ENDPOINTS.maatSorDescription.getConfigAndResponse,
      getOrganisationConfigAndResponse: API_ENDPOINTS.organisation.getConfigAndResponse,
      getOrganisationConfigWithEmptyResponse: API_ENDPOINTS.organisation.getConfigWithEmptyResponse,
      getTeamConfigAndResponse: API_ENDPOINTS.team.getConfigAndResponse,
      getTeamConfigWithEmptyResponse: API_ENDPOINTS.team.getConfigWithEmptyResponse,
      getUserConfigAndResponse: API_ENDPOINTS.user.getConfigAndResponse,
      getUserConfigWithEmptyResponse: API_ENDPOINTS.user.getConfigWithEmptyResponse,
      getInspectionConfigAndResponse: API_ENDPOINTS.inspection.getConfigAndResponse,
      getInspectionCalendarConfigAndResponse: API_ENDPOINTS.inspectionCalendar.getConfigAndResponse,
      getInspectionListConfigForBatch: API_ENDPOINTS.inspectionList.getConfigForBatch
    }

    this.addressService.endpoints = {
      //Note: Do not change the 'addressApiSearchEndpoint' and 'addressApiLgaEndpoint' key names, these are used within dspx-core.
      addressApiSearchEndpoint: API_ENDPOINTS.address.search,
      addressApiLgaEndpoint: API_ENDPOINTS.address.lga
    }

    this.coreService.fileUploadFormIdentifiers = {};
    this.fileService.endpoints = {
      //Note: By default, we use the "Document/FlagForRemoval" endpoint as the fileRemoveEndpoint. This endpoint calls a function that marks the file
      //for deletion and allows the deletion to be deferred to an appropriate time. This is required for the scenario where a form with a file upload
      //control allows edits. If a user edits the form and clicks Remove on an uploaded file, we do not want the file to be immediately deleted
      //from the document management service as the user may not end up saving the form to commit the changes. Therefore, we need to defer the deletion
      //of the file until they save the form, hence the need to mark the file for deletion. If the file can be deleted immediately, use "Document/Remove"
      //as the fileRemoveEndpoint.
      fileUploadEndpoint: API_ENDPOINTS.document.upload,
      fileRemoveEndpoint: API_ENDPOINTS.document.flagForRemoval,
      //fileRemoveEndpoint: API_ENDPOINTS.document.remove,
      fileDownloadEndpoint: `${ApiBaseRoute}/${API_ENDPOINTS.document.download}`
    }

    this.calendarService.endpoints = {
      calendarEventAddEndpoint: '/Calendar/AddEvent',
      calendarEventUpdateEndpoint: '/Calendar/UpdateEvent',
      calendarEventDeleteEndpoint: '/Calendar/DeleteEvent',
      calendarGetDataEndpoint: '/Calendar/GetData'
    };

    this.licenceService.endpoints = {
        fullCalendar: API_ENDPOINTS.licence.fullCalendar
    };

    this.coreService.applicationFunctions = {};

    this.coreService.conditionFunctions = {};

    this.coreService.pageFunctions = {};

    this.coreService.repeatingPageFunctions = {};

    this.coreService.repeatingSectionFunctions = {};

    this.headerConfig = {
      applicationTitle: {
        label: APPLICATION.title,
      },
      applicationSubTitle: {
        label: APPLICATION_SUB_TITLE[APPLICATION_STATUS.ONLINE]
      },
      applicationLogo: {
        source: APPLICATION.logo,
      },
    };

    // Configure the footer
    this.footerConfig = {
      lightModeLogo: '/assets/images/sgv-logo-dark.png',
      darkModeLogo: '/assets/images/sgv-logo.png',
      atsiLogo: '/assets/images/logo-a-tsi-flag.svg',
      prideLogo: '/assets/images/flag-pride.svg',
      prideDeclaration:
        'The Department of Families, Fairness and Housing celebrates, values and includes people of all backgrounds, genders, sexualities, cultures, bodies and abilities.',
      atsiDeclaration:
        'The department acknowledges Aboriginal and Torres Strait Islander people as the Traditional Custodians of the land and acknowledges and pays respect to their Elders, past and present.',
      footerLinkColumns: [
        {
          heading: 'About the site',
          links: [
            {
              label: 'Accessibility',
              altText: 'Accessibility',
              url: 'https://www.dffh.vic.gov.au/accessibility'
            },
            {
              label: 'Privacy statement',
              altText: 'Privacy statement',
              url: 'https://www.dffh.vic.gov.au/privacy-statement'
            },
            {
              label: 'Disclaimer',
              altText: 'Disclaimer',
              url: 'https://www.dffh.vic.gov.au/Disclaimer'
            },
            {
              label: 'Copyright',
              altText: 'Copyright',
              url: 'https://www.dffh.vic.gov.au/Copyright'
            }
          ],
        },
        {
          heading: 'Help with information',
          links: [
            {
              label: 'Funded agency channel',
              altText: 'Funded agency channel',
              url: 'https://fac.dffh.vic.gov.au/',
            },
            {
              label: 'National Relay Service',
              altText: 'National Relay Service',
              url: 'https://www.accesshub.gov.au/',
            }
          ],
        },
      ],
      departmentName: 'Department of Families, Fairness and Housing',
      governmentName: 'State Government of Victoria, Australia',
      showAppLogo: false,
      lightModeAppLogo: '/assets/images/OSCAR_Logo_V__RGB__Navy_Turquoise.svg',
      darkModeAppLogo: '/assets/images/OSCAR_Logo_V__RGB__Navy_Turquoise.svg',
    };

    // Get Logged-in user
    this.serviceWorkerService.waitForServiceWorker()
      .then(waitResult => {

        const userProfile = waitResult.loggedInUserProfile;
        this.logger.log(`${logPrefix}: `, { userProfile });

        const serverAppSettings = userProfile.serverAppSettings;

        this.syncOperationService.setSyncLogBufferMaxSize(serverAppSettings.syncLogBufferMaxSize);
        LogBuffer.setMaxBufferSize(serverAppSettings.webSiteLogBufferMaxSize);

        // Configure the environment banner
        const environmentName = serverAppSettings.environmentName;
        if (environmentName) {
          this.environmentBannerMessage = `You are using the ${environmentName} environment`;
        }
        const environmentBannerBgColor = serverAppSettings.environmentBannerBackgroundColor;
        if (environmentBannerBgColor) {
          this.environmentBannerBgColor = environmentBannerBgColor;
        }
        const environmentBannerColor = serverAppSettings.environmentBannerTextColor;
        if (environmentBannerColor) {
          this.environmentBannerColor = environmentBannerColor;
        }

        // Update the header
        if (userProfile) {
          this.headerConfig.applicationTitle = {
            label: APPLICATION.title,
            url: location.origin,
            target: '_self'
          };
          this.headerConfig.loggedInUser = {
            name: userProfile.fullName,
            imageSource: '/assets/images/avatar.png',
            imageDescription: userProfile.fullName,
            menuSubItems: [
              {
                label: 'Logout',
                icon: 'pi pi-fw pi-sign-out',
                onClickCustomCallback: (e: Event) => {
                  if (this.networkConnectivityService.isOnline()) {
                    // we are using MS Identity so redirect to MicrosoftIdentity Web UI Account Controller action
                    window.open('MicrosoftIdentity/Account/SignOut', '_self');
                  }
                  else {
                    this.dialogService.showSimplePopup('<p>Cannot log out when the system is offline.</p>', 'System Offline', 'Close');
                  }
                }
              }
            ]
          };
          this.headerService.onHeaderUpdated(this.headerConfig);

          // Wire up network connectivity listeners
          this.checkNetworkConnectivityListener = (event: Event) => this.networkConnectivityService.checkConnectivity();
          window.addEventListener('online', this.checkNetworkConnectivityListener);
          window.addEventListener('offline', this.checkNetworkConnectivityListener);
          const networkConnectivityChangeListener = () => this.refreshApplicationStatus();
          this.networkConnectivityService.addConnectivityChangeListener(this.componentClassName, networkConnectivityChangeListener);

          this.networkConnectivityService.checkConnectivity()
            .then(() => {

              // Start sync (if required)
              if (!userProfile?.serverAppSettings?.disableSyncOnStartup && this.networkConnectivityService.isOnline()) {
                const usesServiceWorker = userProfile?.serverAppSettings?.offlineAllowedRoles?.some(role => userProfile?.roles?.includes(role));
                if (usesServiceWorker) {
                  this.syncMessenger.postMessage<SyncRequestMessage>(SYNC_MESSENGER_MESSAGE_TYPES.syncRequest, { requestSource: this.componentClassName, isStartup: true });
                }
              }

              // Configure the menu
              const request: FormConfigurationRequest = {
                apiEndpointKey: API_ENDPOINT_KEYS.getFormConfig,
                templateId: TEMPLATES.menu,
                versionNo: VERSIONS.latest,
                entityId: null,
              };
              this.formConfigurationService
                .getFormConfigAndResponse(request)
                .pipe(take(1))
                .subscribe((data) => {
                  const menuConfig = this.cardSetTransformationService.extractMenuConfigFromFormConfig([data.configData.config]);
                  this.menuService.onMenuLoad(menuConfig);
                });

              // Wire up service worker messages
              this.syncMessenger.addListener<SyncStartedMessage>(SYNC_MESSENGER_MESSAGE_TYPES.syncStarted, message => this.onSyncStarted(message));
              this.syncMessenger.addListener<SyncProgressMessage>(SYNC_MESSENGER_MESSAGE_TYPES.syncProgress, message => this.onSyncProgress(message));
              this.syncMessenger.addListener<SyncEndedMessage>(SYNC_MESSENGER_MESSAGE_TYPES.syncEnded, message => this.onSyncEnded(message));
              this.syncMessenger.addListener<SyncStatusUpdateMessage>(SYNC_MESSENGER_MESSAGE_TYPES.syncStatusUpdate, message => this.onSyncStatusUpdate(message));
            });
        }
      });

    this.dialogService.updatePopupText(
      POP_UP_CODES.common.confirmDocumentDeletion,
      POP_UP_TEXTS.common.confirmDocumentDeletion);

    // Overriding FormNavigationService methods to check the custom section and page types.
    // Custom section and page have different types (card-section-collapsible, card-section-repeating-collapsible, card-set-page-with-collapsible-sections).
    this.formNavigationService.isSectionType = (typeName: string) => this.sectionTypes.includes(typeName);
    this.formNavigationService.isPageType = (typeName: string) => this.pageTypes.includes(typeName);
    this.formNavigationService.isRepeatingSectionType = (typeName: string) => this.repeatingSectionTypes.includes(typeName);

    this.routeBreadcrumbNavigation();

    this.logger.log(`${logPrefix}: leave`);
  }

  ngOnDestroy(): void {

    this.syncMessenger.removeListener(SYNC_MESSENGER_MESSAGE_TYPES.syncStarted);
    this.syncMessenger.removeListener(SYNC_MESSENGER_MESSAGE_TYPES.syncProgress);
    this.syncMessenger.removeListener(SYNC_MESSENGER_MESSAGE_TYPES.syncEnded);
    this.syncMessenger.removeListener(SYNC_MESSENGER_MESSAGE_TYPES.syncStatusUpdate);
    this.syncMessenger.destroy();

    this.syncOperationService.unregisterHandler(this.componentClassName);

    window.removeEventListener('online', this.checkNetworkConnectivityListener);
    window.removeEventListener('offline', this.checkNetworkConnectivityListener);
    this.checkNetworkConnectivityListener = null;
    this.networkConnectivityService.removeConnectivityChangeListener(this.componentClassName);
  }

  private onSyncStarted(message: SyncStartedMessage): void {
    this.refreshApplicationStatus(APPLICATION_STATUS.ONLINE_AND_SYNC_IN_PROGRESS);
    this.syncOperationService.onStarted(SYNC_OPERATION_NAMES.sync, message.request);
  }

  private onSyncProgress(message: SyncProgressMessage): void {
    this.syncOperationService.onProgress(SYNC_OPERATION_NAMES.sync, message.request, message.progress);
  }

  private onSyncEnded(message: SyncEndedMessage): void {
    this.syncOperationService.onEnded(SYNC_OPERATION_NAMES.sync, message.request, message.result);
  }

  private onSyncStatusUpdate(message: SyncStatusUpdateMessage): void {
    this.refreshApplicationStatus(message.isSyncRequired ? APPLICATION_STATUS.ONLINE_AND_SYNC_REQUIRED : null);
  }

  private refreshApplicationStatus(applicationStatus?: APPLICATION_STATUS): void {

    applicationStatus ??= this.networkConnectivityService.isOnline() ? APPLICATION_STATUS.ONLINE : APPLICATION_STATUS.OFFLINE;

    // Update the application subtitle
    const applicationSubTitleLabel = APPLICATION_SUB_TITLE[applicationStatus];
    this.headerConfig.applicationSubTitle.label = applicationSubTitleLabel;
    this.headerService.onHeaderUpdated(this.headerConfig);

    // Update the header background colour
    const setHeaderBackgroundColour = (cssClassName: string) => {
      const htmlElement = document.querySelector(`${AppSelector} ${HeaderComponentSelector} .${cssClassName}`) as HTMLElement;
      if (htmlElement) {
        htmlElement.style.backgroundColor = APPLICATION_HEADER_BACKGROUND_COLOUR[applicationStatus];
      }
    }
    for (const headerComponentClass of HeaderComponentClasses) {
      setHeaderBackgroundColour(headerComponentClass);
    }
  }

  // Intercept breadcrumb clicks to use Angular routing instead of 
  // full page reload which will trigger the sync code
  private routeBreadcrumbNavigation(): void {
    this.breadcrumbService.crumbs$.subscribe( () => {
      setTimeout(() => {
        document.querySelectorAll(`${AppSelector} ${BreadcrumbSelector} a`)
          ?.forEach((anchor) => {
            anchor.addEventListener('click', event => {
              event.preventDefault();
              const url = anchor.getAttribute('href');
              if (url) {
                this.router.navigateByUrl('/' + url);
              }
            });
          });
      });
    });
  }
}
